import { Component, Host, h, Prop, Watch, Element } from '@stencil/core';
import { ResponsiveVideos } from '../../helpers/utils/interfaces';
import { endpoints } from '../../../.conf/resources';

@Component({
  tag: 'cms-moving-image',
  styleUrl: 'cms-moving-image.scss',
  shadow: true,
})
export class CmsMovingImage {
  private bgImage = 'scratch-bg-brand1.webp';
  private imageSrc = `${endpoints.assetPath}/${this.bgImage}`;

  private responsiveVideos: ResponsiveVideos;
  private videoElement: HTMLVideoElement;
  private videoBreakpoints = {
    desktop: 1280,
    tablet: 720,
    mobile: 480,
    current: 0,
  };
  private videoSources = {
    mp4: null,
    webm: null,
  };

  /**
   * set the Responsive Videos in mp4 and webm format
   */
  @Prop({ mutable: true }) videos!: ResponsiveVideos | string;
  /**
   * Set the Headline
   */
  @Prop({ mutable: true }) headline: string;
  /**
   *Set the subheading
   */
  @Prop({ mutable: true }) subheading: string;
  /**
   * Set the target URL for the Video Link
   */
  @Prop({ mutable: true }) targetUrl: string;
  /**
   * Set the target tab for the Video Link like _self or _blank
   */
  @Prop({ mutable: true }) targetWindow: string;
  /**
   * Set the Background Color for use in Breakpoint >= 1920px
   */
  @Prop({ mutable: true }) backgroundColor: string;

  @Watch('videos')
  private videosWatcher(newValue: ResponsiveVideos | string) {
    if (typeof newValue === 'string') {
      this.responsiveVideos = JSON.parse(newValue);
    }
  }

  @Element() videoNode: HTMLVideoElement;

  private renderHeadline() {
    return (
      <div class={'headline-wrapper'}>
        <div
          class={'headline-wrapper-inner'}
          style={{ backgroundImage: `url(${this.imageSrc})` }}
        >
          {this.headline !== undefined ? (
            <cms-headline
              /*      style={{ '--cms-theme-text-color': '#fff' }} */
              headlineText={this.headline}
              headlineTag='h1'
              headline-class='moving-image-headline'
              colorToken={'--cms-semanticColors-primary-textOnBackground'}
            />
          ) : null}
          {this.subheading != undefined ? (
            <div class={'moving-image-subheading'}>
              {this.subheading}{' '}
              <span class={'cta-svg'}>
                {this.targetUrl ? (
                  <cms-icon
                    style={{
                      '--cms-theme-svg-color':
                        'var(--cms-semanticColors-base-inverted-medium-iconOnBackground)',
                    }}
                    size='xl'
                    colorToken={'--cms-semanticColors-primary-iconOnBackground'}
                    icon='arrowShaftRight'
                  ></cms-icon>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  private renderContent() {
    return (
      <div class={'moving-image-content'}>
        {this.responsiveVideos != undefined ? this.renderVideoHtml() : null}
        {this.headline != undefined || this.subheading != undefined
          ? this.renderHeadline()
          : null}
      </div>
    );
  }

  private renderVideoHtml() {
    const { mp4, webm }: { mp4: string | null; webm: string | null } =
      this.videoSources;
    return (
      <video class={'moving-image-video'}>
        {mp4 != null ? <source type='video/mp4' src={mp4} /> : null}
        {webm != null ? <source type='video/webm' src={webm} /> : null}
      </video>
    );
  }

  private videoOptions(video: HTMLVideoElement) {
    video.autoplay = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.loop = true;
    video.muted = true;
  }

  private async loadVideo(video: HTMLVideoElement) {
    try {
      video.load();
      await this.playVideo(video);
    } catch (error) {
      console.info('error: ', error);
    }
  }

  private async playVideo(video: HTMLVideoElement) {
    try {
      await video.play();
    } catch (error) {
      console.info('error: ', error);
    }
  }

  private setCurrentBreakpoint() {
    const windowViewWidth = window.innerWidth;
    if (windowViewWidth >= this.videoBreakpoints.desktop) {
      return 1280;
    }
    return windowViewWidth >= this.videoBreakpoints.tablet ? 720 : 480;
  }

  private setVideoSrc(src: string, type: string) {
    if (type === 'mp4') {
      this.videoSources.mp4 = src;
    } else {
      this.videoSources.webm = src;
    }
  }

  private getVideosIsMatchingBreakpoint(responsiveVideos: ResponsiveVideos) {
    const matchVideoBreakpoint = this.checkVideoIsMatchingBreakpoint(
      responsiveVideos,
    )
      ? this.videoBreakpoints.current
      : 720;
    return Object.entries(responsiveVideos).map(([key, videoSrc]) => {
      const [videoSize, type] = key.split('-');
      if (parseInt(videoSize) === matchVideoBreakpoint) {
        return this.setVideoSrc(videoSrc, type);
      }
    });
  }

  private checkVideoIsMatchingBreakpoint(responsiveVideos: ResponsiveVideos) {
    const searchKeyMp4 = `${this.videoBreakpoints.current}-mp4`;
    const searchKeyWebm = `${this.videoBreakpoints.current}-webm`;
    return (
      Object.prototype.hasOwnProperty.call(responsiveVideos, searchKeyMp4) ||
      Object.prototype.hasOwnProperty.call(responsiveVideos, searchKeyWebm)
    );
  }

  connectedCallback(): void {
    this.videosWatcher(this.videos);
    this.videoBreakpoints.current = this.setCurrentBreakpoint();
    this.getVideosIsMatchingBreakpoint(this.responsiveVideos);
  }

  componentDidLoad() {
    this.videoElement = this.videoNode.shadowRoot.querySelector(
      '.moving-image-video',
    );
    this.videoOptions(this.videoElement);
    this.loadVideo(this.videoElement);
  }

  render() {
    return (
      <Host
        class={'moving-image'}
        style={
          this.backgroundColor
            ? { ['background']: `var(${this.backgroundColor})` }
            : null
        }
      >
        <div class={'moving-image-container'}>
          {this.targetUrl != undefined ? (
            <a
              href={this.targetUrl}
              class={'moving-image-link'}
              title={this.headline ? this.headline : ''}
              target={this.targetWindow ? this.targetWindow : '_blank'}
            >
              {this.renderContent()}
            </a>
          ) : (
            this.renderContent()
          )}
        </div>
      </Host>
    );
  }
}
